import { Alert, Button, Center, Switch, Text, VStack } from '@chakra-ui/react'
import { useRouter } from 'next/router'
import { BaseSyntheticEvent, PropsWithChildren, useEffect } from 'react'

import { useRouterStore } from '~/components/hooks/RouterStore'
import { useAdminContext } from '~/components/hooks/useAdminContext'
import { LocalStorageKey, useLocalStorage } from '~/components/hooks/useLocalStorage'
import { setAccessToken } from '~/data/accessToken'
import { getWriteMode, setWriteMode } from '~/data/adminWriteMode'
import { apolloClient } from '~/data/apollo'
import { AdminContext } from '~/util/AdminContext'
import { isDev } from '~/util/env'
import { clearLocalStoragePropertyRelatedValues } from '~/util/property'
import { AdminRoute, generateAdminRoute, makeURLStringFromURLObject } from '~/util/route'

export const AdminWrapper = ({ children }: PropsWithChildren<unknown>) => {
  const router = useRouter()
  const routerStore = useRouterStore()
  const { adminUser, impersonatedUser, isImpersonating } = useAdminContext()
  const showAdminContainer = isImpersonating
  const [writeEnabledRaw, setWriteEnabled] = useLocalStorage(LocalStorageKey.ADMIN_WRITE_MODE)
  // Initial load of this value is null (typeof 'object') => should be render `false`
  // Page refresh reads raw value from LS (typeof 'string') => should do string compare to determine value
  // Intrapage toggles / reads (typeof 'boolean') => should use the boolean value
  // This line captures the `boolean|object` as a compare with true:boolean
  // And captures `string` as a compare with `true`:string
  const writeEnabled =
    typeof writeEnabledRaw === 'string' ? writeEnabledRaw === `true` : writeEnabledRaw === true

  useEffect(() => {
    if (writeEnabled !== getWriteMode()) setWriteMode(writeEnabled)
  }, [writeEnabled])

  const goBackToAdmin = () => {
    fetch(`${process.env.NEXT_PUBLIC_BASE_API_URL}/refresh_token`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userSid: adminUser?.sid,
      }),
    })
      .then(response => response.json())
      .then(data => {
        const accessToken = data.accessToken
        if (accessToken) {
          setAccessToken(accessToken)
          localStorage?.removeItem(LocalStorageKey.ADMIN_DATA)
          localStorage?.removeItem(LocalStorageKey.ADMIN_WRITE_MODE)
          // Clear property and portfolio related local storage values
          clearLocalStoragePropertyRelatedValues()
          // Use full page navigation to ensure the user context is refreshed with the admin token.
          // Client-side router.push() would cause a race condition where the old impersonated user
          // context is still in memory, triggering the non-admin redirect in admin.tsx.
          window.location.href =
            '/' +
            makeURLStringFromURLObject(
              generateAdminRoute(AdminRoute.users, { firm: impersonatedUser.firm })
            )
        }
      })
      .catch(() => alert('error'))
  }

  const onLogOut = () => {
    void fetch(`${process.env.NEXT_PUBLIC_BASE_API_URL}/logout`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    })
      .then(() => {
        setAccessToken('')
        localStorage?.removeItem(LocalStorageKey.ADMIN_DATA)
        localStorage?.removeItem(LocalStorageKey.ADMIN_WRITE_MODE)
        // Clear property and portfolio related local storage values
        localStorage?.removeItem(LocalStorageKey.PROPERTY_SID)
        localStorage?.removeItem(LocalStorageKey.PORTFOLIO_SID)
        localStorage?.removeItem(LocalStorageKey.RENEWALS_PROPERTY_SID)
        void apolloClient.cache.reset({ discardWatches: true })
        routerStore.clearEverything()
        router.push({ pathname: 'start' }).catch(e => console.error(e))
      })
      .catch(() => alert('error'))
  }

  const onChangeWriteMode = (e: BaseSyntheticEvent) => {
    setWriteMode(e?.target?.checked)
    setWriteEnabled(e?.target?.checked)
  }

  return (
    <AdminContext.Provider value={{}}>
      <VStack width="full" spacing="0">
        {children}
        {showAdminContainer && (
          <Alert
            status={isDev ? 'success' : writeEnabled ? 'error' : 'warning'}
            variant="solid"
            style={{ width: '100%', height: 'auto', opacity: '1' }}
          >
            <Center w={'100%'} h="18px">
              <Text fontSize="xs" marginRight={3}>
                <b>Write mode</b>
                <Switch
                  defaultChecked={writeEnabled}
                  id="write_mode"
                  marginLeft={1}
                  size={'sm'}
                  onChange={onChangeWriteMode}
                />
              </Text>
              <Text fontSize="sm" marginRight={3}>
                Impersonating <b>{impersonatedUser?.name}</b> @{' '}
                <b>{impersonatedUser?.firmName ?? impersonatedUser?.firm}</b>
              </Text>
              <Button
                colorScheme="primary"
                variant={'outline'}
                size="xs"
                marginRight={2}
                onClick={goBackToAdmin}
              >
                {adminUser?.name}
              </Button>
              <Button colorScheme="primary" variant={'outline'} size="xs" onClick={onLogOut}>
                Log out
              </Button>
            </Center>
          </Alert>
        )}
      </VStack>
    </AdminContext.Provider>
  )
}
